import type { CardType } from '@goaltree/backend/convex/lib/shareCards'

// Opens the story share-card sheet from anywhere (toasts, menus, celebrations);
// <ShareCardHost/> in the authenticated layout listens.

export type ShareCardTarget = { type: CardType; ref: string }
export type ShareCardRequest = ShareCardTarget & {
  // Other cards for the same subject, offered as tabs (e.g. streak / level).
  alt?: ShareCardTarget[]
  // Habit the sheet was opened for: adds its best / progress cards and this month's wrap.
  habit?: string
  // Open with "Include photos" already on.
  photos?: boolean
}

const EVENT = 'goaltree:share-card'

export function openShareCard(req: ShareCardRequest) {
  window.dispatchEvent(new CustomEvent<ShareCardRequest>(EVENT, { detail: req }))
}

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

// Today's share: the day card first, then the overall streak, this week and this
// month. Cards with nothing to show yet drop out of the picker.
export function dayShareCards(accentRef: string): ShareCardRequest {
  const now = new Date()
  const monday = new Date(now)
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7))
  return {
    type: 'day',
    ref: accentRef,
    alt: [
      { type: 'streak', ref: 'all' },
      { type: 'week', ref: ymd(monday) },
      { type: 'wrap', ref: ymd(now).slice(0, 7) },
    ],
  }
}

export function onShareCard(fn: (req: ShareCardRequest) => void) {
  const h = (e: Event) => fn((e as CustomEvent<ShareCardRequest>).detail)
  window.addEventListener(EVENT, h)
  return () => window.removeEventListener(EVENT, h)
}

export const STREAK_SHARE_MILESTONES = [7, 14, 30, 60, 100, 365]
// Milestones big enough for the full-screen celebration; the rest get a toast.
export const STREAK_CELEBRATE_MILESTONES = [7, 30, 100, 365]
export const CELEBRATION_GAP_MS = 7 * 86_400_000

export type CardLook = { theme: 'dark' | 'light'; accent: string }
export type MintedCard = {
  imageUrl: string
  link: string
  styles: string[]
  options: CardLook & { style: string }
  // Hex of the "category" swatch for this card.
  accent: string
  opens?: boolean
  // AI coach's name when the card can say "Coached by"; unset hides "Show my coach".
  coach?: string
}

export const cardImageUrl = (
  c: MintedCard,
  style: string,
  l: CardLook,
  w?: number,
  photos?: boolean,
  coach = true,
) =>
  `${c.imageUrl}&style=${style}&theme=${l.theme}&accent=${l.accent}${w ? `&w=${w}` : ''}${photos ? '&photos=1' : ''}${coach && c.coach ? '&coach=1' : ''}`
